import { useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Box, CircularProgress, Typography } from "@mui/material";

import { Column, Row } from "../../Layout/utils";
import { fetchMLMetrics, resetCustomModel } from "./services";
import ActivateModelDialog from "./ActivateModelDialog";
import ActiveModelBanner from "./ActiveModelBanner";
import ConfusionMatrix from "./ConfusionMatrix";
import CustomModelTrainer from "./CustomModelTrainer";
import FeatureRanking from "./FeatureRanking";
import MetricsGuide from "./MetricsGuide";
import MetricsRadar from "./MetricsRadar";
import MetricsTable, { MetricsTableRow } from "./MetricsTable";
import PredictionBreakdown from "./PredictionBreakdown";
import TrainingTiles from "./TrainingTiles";

const MLAnalysis = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [pollEnabled, setPollEnabled] = useState(false);
  const [confirmModelName, setConfirmModelName] = useState<string | null>(null);

  const {
    data: metrics,
    isLoading,
    isError,
  } = useQuery("ml-analysis-metrics", fetchMLMetrics, {
    refetchOnWindowFocus: false,
    refetchInterval: pollEnabled ? 5000 : false,
    onSuccess: (data) => {
      const s = data.find(
        (m) => m.model_name === "prob_vente_custom"
      )?.training_status;
      if (s === "done" || s === "error") setPollEnabled(false);
    },
  });

  const customStatus = metrics?.find(
    (m) => m.model_name === "prob_vente_custom"
  )?.training_status;

  const resetMutation = useMutation(resetCustomModel, {
    onSuccess: () => {
      setPollEnabled(false);
      queryClient.invalidateQueries("ml-analysis-metrics");
    },
  });

  const standardModel = metrics?.find((m) => m.model_name === "prob_vente");
  const customModel = metrics?.find(
    (m) => m.model_name === "prob_vente_custom" && m.training_status === "done"
  );
  const activeModel = metrics?.find((m) => m.is_active);

  const tableRows: MetricsTableRow[] = [
    ...(standardModel ? [{ m: standardModel, name: "prob_vente" }] : []),
    ...(customModel ? [{ m: customModel, name: "prob_vente_custom" }] : []),
  ];

  if (isLoading) {
    return (
      <Row sx={{ justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Row>
    );
  }

  if (isError || !metrics || metrics.length === 0) {
    return (
      <Column sx={{ p: 4 }}>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          {intl.formatMessage({ id: "ML_ANALYSIS_NO_DATA" })}
        </Typography>
      </Column>
    );
  }

  return (
    <Column sx={{ p: 3, gap: 1 }}>
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {intl.formatMessage({ id: "ML_ANALYSIS_TITLE" })}
        </Typography>
        {activeModel && <TrainingTiles model={activeModel} />}
      </Box>
      {activeModel && <ActiveModelBanner model={activeModel} />}
      {tableRows.length > 0 && (
        <MetricsTable rows={tableRows} onSelectModel={setConfirmModelName} />
      )}
      <MetricsGuide />
      {activeModel && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <MetricsRadar model={activeModel} />
          <ConfusionMatrix model={activeModel} />
        </Box>
      )}
      <PredictionBreakdown activeFeatures={activeModel?.features} />
      {activeModel && <FeatureRanking model={activeModel} />}
      <CustomModelTrainer
        trainingStatus={customStatus}
        modelFeatures={customModel?.features}
        onStarted={() => setPollEnabled(true)}
        onReset={() => resetMutation.mutate()}
      />
      <ActivateModelDialog
        modelName={confirmModelName}
        onClose={() => setConfirmModelName(null)}
      />
    </Column>
  );
};

export default MLAnalysis;
